import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import {
  mergeStatusOptionsWithDefaults,
  normalizeFieldOptions,
} from "../../../utils/taskTemplateChipStyles";
import { normalizeTaskListRow, normalizeTaskListRows } from "../../../utils/taskDeadlineDisplay";
import { sortGroupedTasksData, sortTaskTableRows } from "../../../utils/taskTableRowSort";

const normalizeTaskFieldOptionsPayload = (payload) => {
  const data = payload || {};
  return {
    typeOptions: normalizeFieldOptions(data.typeOptions, "type"),
    statusOptions: mergeStatusOptionsWithDefaults(data.statusOptions),
    priorityOptions: normalizeFieldOptions(data.priorityOptions, "priority"),
  };
};

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const authHeaders = async () => {
  const token = await getAuthToken();
  if (!token) throw new Error("No token found");
  return { Authorization: `Bearer ${token}` };
};

// Debouncing for API calls to prevent spam. These list thunks are re-dispatched with
// identical params by reloadTasks() after almost every task mutation (create/patch/delete)
// — unlike a background/permissions thunk, a long window here risks silently dropping a
// legitimate refresh after a second, distinct mutation a few seconds later. Kept short:
// long enough to absorb same-tick duplicate mounts (the actual bug), short enough to never
// plausibly collide with two separate user-triggered mutations.
const debounceMap = new Map();
const DEBOUNCE_TIME = 600; // 0.6 seconds

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);

  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }

  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetTasksCaches = () => {
  debounceMap.clear();
};

/** Grouped view repeats team-assigned tasks in every member bucket; badges use one row per task id. */
const flattenGroupedTasksUnique = (grouped) => {
  if (!grouped) return [];
  const seen = new Set();
  const flat = [];
  const addUnique = (rows) => {
    (rows || []).forEach((row) => {
      const id = String(row?.id || row?._id || "");
      if (!id || seen.has(id)) return;
      seen.add(id);
      flat.push(row);
    });
  };
  addUnique(grouped.leaderTasks);
  addUnique(grouped.unassignedTasks);
  (grouped.members || []).forEach((bucket) => addUnique(bucket.tasks));
  return flat;
};

export const fetchTasks = createAsyncThunk(
  "tasks/fetchList",
  async (
    { page = 1, limit = 25, search = "", teamId = "", status = "", mine = false } = {},
    { rejectWithValue }
  ) => {
    if (shouldDebounce(`fetchTasks_${page}_${limit}_${search}_${teamId}_${status}_${mine}`)) {
      return rejectWithValue("Debounced");
    }
    try {
      const headers = await authHeaders();
      const params = { page, limit, search };
      if (teamId) params.teamId = teamId;
      if (status && status !== "all") params.status = status;
      if (mine) params.mine = "true";
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks`, {
        headers,
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskStatusCounts = createAsyncThunk(
  "tasks/fetchStatusCounts",
  async ({ teamId = "", mine = false } = {}, { rejectWithValue }) => {
    if (shouldDebounce(`fetchTaskStatusCounts_${teamId}_${mine}`)) {
      return rejectWithValue("Debounced");
    }
    try {
      const headers = await authHeaders();
      const params = {};
      if (teamId) params.teamId = teamId;
      if (mine) params.mine = "true";
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks/status-counts`, {
        headers,
        params,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTasksTeamsOverview = createAsyncThunk(
  "tasks/fetchTeamsOverview",
  async (_, { rejectWithValue }) => {
    if (shouldDebounce("fetchTasksTeamsOverview")) {
      return rejectWithValue("Debounced");
    }
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks/teams-overview`, { headers });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTasksGrouped = createAsyncThunk(
  "tasks/fetchGrouped",
  async ({ teamId, search = "" } = {}, { rejectWithValue }) => {
    if (shouldDebounce(`fetchTasksGrouped_${teamId}_${search}`)) {
      return rejectWithValue("Debounced");
    }
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks/grouped`, {
        headers,
        params: { teamId, search },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskStatus = createAsyncThunk(
  "tasks/patchStatus",
  async ({ id, status, source }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const body = { status };
      if (source) body.source = source;
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/tasks/${id}/status`,
        body,
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskPriority = createAsyncThunk(
  "tasks/patchPriority",
  async ({ id, priority }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/tasks/${id}/priority`,
        { priority },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const patchTaskType = createAsyncThunk(
  "tasks/patchType",
  async ({ id, templateType }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(
        `${SERVER_DOMAIN}/api/tasks/${id}/type`,
        { templateType },
        { headers }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskById = createAsyncThunk(
  "tasks/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks/${id}`, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTask = createAsyncThunk(
  "tasks/create",
  async (payload, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.post(`${SERVER_DOMAIN}/api/tasks`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTask = createAsyncThunk(
  "tasks/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.patch(`${SERVER_DOMAIN}/api/tasks/${id}`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  "tasks/delete",
  async (id, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      await axios.delete(`${SERVER_DOMAIN}/api/tasks/${id}`, { headers });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTaskFieldOptions = createAsyncThunk(
  "tasks/fetchFieldOptions",
  async (_, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.get(`${SERVER_DOMAIN}/api/tasks/field-options`, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTaskFieldOptions = createAsyncThunk(
  "tasks/updateFieldOptions",
  async (payload, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const response = await axios.put(`${SERVER_DOMAIN}/api/tasks/field-options`, payload, { headers });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const uploadTaskFile = createAsyncThunk(
  "tasks/uploadFile",
  async (file, { rejectWithValue }) => {
    try {
      const headers = await authHeaders();
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${SERVER_DOMAIN}/api/tasks/upload/file`, formData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  rows: [],
  pagination: { page: 1, limit: 25, total: 0 },
  scope: { isOwner: false, viewAll: false, viewAssignedOnly: false, ledTeamIds: [] },
  statusCounts: { all: 0, pending: 0, inProgress: 0, cancelled: 0, completed: 0 },
  statusCountsLoading: false,
  teamsOverview: [],
  teamsOverviewLoading: false,
  grouped: null,
  groupedLoading: false,
  loading: false,
  error: null,
  selectedTask: null,
  selectedLoading: false,
  uploadLoading: false,
  saving: false,
  deleting: false,
  patching: false,
  fieldOptions: { typeOptions: [], statusOptions: [], priorityOptions: [] },
  fieldOptionsLoading: false,
  fieldOptionsSaving: false,
};


const taskDetailToRowPatch = (task) => {
  if (!task) return null;
  const row = {
    id: task._id || task.id,
    name: task.name,
    templateType: task.templateType || "",
    status: task.status || "",
    priority: task.priority || "",
    deadlineAt: task.deadline || null,
    recurringEnabled:
      task.enabledSections?.recurring === true &&
      task.recurringConfig != null &&
      typeof task.recurringConfig === "object",
    missedCycles: Number(task.recurringState?.missedCycles) || 0,
    assignees: (task.assignees || []).map((x) => ({
      id: String(x?.id || "").trim(),
      type: x?.type === "team" ? "team" : "teammember",
      name: String(x?.name || "").trim(),
    })).filter((p) => p.id),
    assigne: (task.assignees || []).map((x) => x.name).join(", ") || "—",
    teamIds: (task.teamIds || []).map((id) => String(id)).filter(Boolean),
    owners: (task.owners || []).map((x) => x.name).join(", ") || "—",
    participants: (task.participants || []).map((x) => x.name).join(", ") || "—",
    createdBy: task.createdBy?.name || "",
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    updatedBy: task.updatedBy?.name || "",
    teamId: task.teamId ? String(task.teamId) : null,
    _accessFlags: task._accessFlags,
  };
  return normalizeTaskListRow(row);
};

const patchTaskRowInList = (rows, taskId, rowPatch, { preserveAccessFlags = false, insertIfMissing = false } = {}) => {
  if (!Array.isArray(rows)) return;
  const idx = rows.findIndex((row) => String(row.id || row._id) === taskId);
  if (idx === -1) {
    if (insertIfMissing) rows.push(rowPatch);
    return;
  }
  rows[idx] = preserveAccessFlags
    ? { ...rows[idx], ...rowPatch, _accessFlags: rows[idx]._accessFlags }
    : { ...rows[idx], ...rowPatch };
};

const removeTaskRowFromList = (rows, taskId) => {
  if (!Array.isArray(rows)) return;
  const idx = rows.findIndex((row) => String(row.id || row._id) === taskId);
  if (idx !== -1) rows.splice(idx, 1);
};

/** Apply list row patch to flat `rows` and grouped buckets (leader / members / unassigned). */
const applyRowPatchToTaskState = (state, rowPatch, taskId, options = {}) => {
  const flatOptions = { ...options, insertIfMissing: options.insertIfMissing === true };
  patchTaskRowInList(state.rows, taskId, rowPatch, flatOptions);
  if (!state.grouped) return;
  const groupedOptions = { ...options, insertIfMissing: false };
  const patchGrouped = (rows) => patchTaskRowInList(rows, taskId, rowPatch, groupedOptions);
  patchGrouped(state.grouped.leaderTasks);
  patchGrouped(state.grouped.unassignedTasks);
  (state.grouped.members || []).forEach((bucket) => patchGrouped(bucket.tasks));
};

const removeTaskFromTaskState = (state, taskId) => {
  removeTaskRowFromList(state.rows, taskId);
  if (!state.grouped) return;
  const removeGrouped = (rows) => removeTaskRowFromList(rows, taskId);
  removeGrouped(state.grouped.leaderTasks);
  removeGrouped(state.grouped.unassignedTasks);
  (state.grouped.members || []).forEach((bucket) => removeGrouped(bucket.tasks));
};

const sortTaskListState = (state) => {
  state.rows = sortTaskTableRows(state.rows);
  if (state.grouped) {
    state.grouped = sortGroupedTasksData(state.grouped);
  }
};

const mergeRowFromTask = (state, task) => {
  const rowPatch = taskDetailToRowPatch(task);
  if (!rowPatch) return;
  const taskId = String(rowPatch.id);
  applyRowPatchToTaskState(state, rowPatch, taskId);
  sortTaskListState(state);
};

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    clearTaskError(state) {
      state.error = null;
    },
    clearSelectedTask(state) {
      state.selectedTask = null;
    },
    setTasksPage(state, action) {
      state.pagination.page = action.payload;
    },
    setTasksLimit(state, action) {
      state.pagination.limit = action.payload;
    },
    applyTaskRealtimeUpdate(state, action) {
      const task = action.payload;
      if (!task) return;
      const taskId = String(task._id || task.id || "");
      if (!taskId) return;

      if (state.selectedTask && String(state.selectedTask._id || state.selectedTask.id) === taskId) {
        state.selectedTask = {
          ...task,
          _accessFlags: state.selectedTask._accessFlags,
        };
      }

      const rowPatch = taskDetailToRowPatch(task);
      if (!rowPatch) return;
      // `state.rows` is now a server-scoped/paginated page (search/status/mine/page/limit
      // all applied server-side) — unlike the old ~500-row snapshot, we can no longer tell
      // here whether a task NOT already in the loaded page actually belongs on it (right
      // scope, right status, right sort position). Only patch rows already loaded; a task
      // this client doesn't know about yet will show up on the next natural refetch.
      applyRowPatchToTaskState(state, rowPatch, taskId, {
        preserveAccessFlags: true,
        insertIfMissing: false,
      });
      sortTaskListState(state);
    },
    applyTaskRealtimeDelete(state, action) {
      const taskId = String(action.payload || "");
      if (!taskId) return;
      removeTaskFromTaskState(state, taskId);
      if (
        state.selectedTask &&
        String(state.selectedTask._id || state.selectedTask.id) === taskId
      ) {
        state.selectedTask = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskStatusCounts.pending, (state) => {
        state.statusCountsLoading = true;
      })
      .addCase(fetchTaskStatusCounts.fulfilled, (state, action) => {
        state.statusCountsLoading = false;
        state.statusCounts = action.payload || state.statusCounts;
      })
      .addCase(fetchTaskStatusCounts.rejected, (state) => {
        // "Debounced" is not a real error — statusCounts is left untouched either way.
        state.statusCountsLoading = false;
      })
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = sortTaskTableRows(normalizeTaskListRows(action.payload.data || []));
        state.pagination = action.payload.pagination || state.pagination;
        state.scope = action.payload.scope || state.scope;
        state.grouped = null;
      })
      .addCase(fetchTasksTeamsOverview.pending, (state) => {
        state.teamsOverviewLoading = true;
      })
      .addCase(fetchTasksTeamsOverview.fulfilled, (state, action) => {
        state.teamsOverviewLoading = false;
        state.teamsOverview = action.payload.data || [];
      })
      .addCase(fetchTasksTeamsOverview.rejected, (state, action) => {
        state.teamsOverviewLoading = false;
        if (action.payload !== "Debounced") {
          state.teamsOverview = [];
        }
      })
      .addCase(fetchTasksGrouped.pending, (state) => {
        state.groupedLoading = true;
        state.loading = true;
      })
      .addCase(fetchTasksGrouped.fulfilled, (state, action) => {
        state.groupedLoading = false;
        state.loading = false;
        const groupedRaw = action.payload.data || null;
        const grouped = groupedRaw
          ? sortGroupedTasksData({
              ...groupedRaw,
              leaderTasks: normalizeTaskListRows(groupedRaw.leaderTasks || []),
              unassignedTasks: normalizeTaskListRows(groupedRaw.unassignedTasks || []),
              members: (groupedRaw.members || []).map((bucket) => ({
                ...bucket,
                tasks: normalizeTaskListRows(bucket.tasks || []),
              })),
            })
          : null;
        state.grouped = grouped;
        state.rows = sortTaskTableRows(flattenGroupedTasksUnique(grouped));
      })
      .addCase(fetchTasksGrouped.rejected, (state, action) => {
        state.groupedLoading = false;
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      .addCase(patchTaskStatus.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskStatus.fulfilled, (state, action) => {
        state.patching = false;
        mergeRowFromTask(state, action.payload);
      })
      .addCase(patchTaskStatus.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(patchTaskPriority.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskPriority.fulfilled, (state, action) => {
        state.patching = false;
        mergeRowFromTask(state, action.payload);
      })
      .addCase(patchTaskPriority.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(patchTaskType.pending, (state) => {
        state.patching = true;
      })
      .addCase(patchTaskType.fulfilled, (state, action) => {
        state.patching = false;
        mergeRowFromTask(state, action.payload);
      })
      .addCase(patchTaskType.rejected, (state, action) => {
        state.patching = false;
        state.error = action.payload;
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      .addCase(fetchTaskById.pending, (state) => {
        state.selectedLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskById.fulfilled, (state, action) => {
        state.selectedLoading = false;
        state.selectedTask = action.payload;
      })
      .addCase(fetchTaskById.rejected, (state, action) => {
        state.selectedLoading = false;
        state.error = action.payload;
      })
      .addCase(uploadTaskFile.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadTaskFile.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadTaskFile.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload;
      })
      .addCase(createTask.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(createTask.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(updateTask.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateTask.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteTask.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })
      .addCase(fetchTaskFieldOptions.pending, (state) => {
        state.fieldOptionsLoading = true;
        state.error = null;
      })
      .addCase(fetchTaskFieldOptions.fulfilled, (state, action) => {
        state.fieldOptionsLoading = false;
        state.fieldOptions = action.payload || state.fieldOptions;
      })
      .addCase(fetchTaskFieldOptions.rejected, (state, action) => {
        state.fieldOptionsLoading = false;
        state.error = action.payload;
      })
      .addCase(updateTaskFieldOptions.pending, (state) => {
        state.fieldOptionsSaving = true;
        state.error = null;
      })
      .addCase(updateTaskFieldOptions.fulfilled, (state, action) => {
        state.fieldOptionsSaving = false;
        state.fieldOptions = normalizeTaskFieldOptionsPayload(action.payload);
      })
      .addCase(updateTaskFieldOptions.rejected, (state, action) => {
        state.fieldOptionsSaving = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearTaskError,
  clearSelectedTask,
  applyTaskRealtimeUpdate,
  applyTaskRealtimeDelete,
  setTasksPage,
  setTasksLimit,
} = tasksSlice.actions;
export default tasksSlice.reducer;
